// frontend/src/context/index.js - ALL CONTEXTS CONSOLIDATED IN ONE FILE

import React, { createContext, useState, useContext, useEffect, useCallback, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import ApiService from '../services';
import { formatCurrency as sharedFormatCurrency, getCurrencyCode } from '../utils';

// =============================================
// APP CONTEXT
// =============================================

// App Action Types
const APP_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_RESTAURANT_INFO: 'SET_RESTAURANT_INFO',
  SET_OPERATING_HOURS: 'SET_OPERATING_HOURS',
  SET_PUBLIC_SETTINGS: 'SET_PUBLIC_SETTINGS',
  SET_MENU_CATEGORIES: 'SET_MENU_CATEGORIES',
  SET_SPECIAL_OFFERS: 'SET_SPECIAL_OFFERS',
  SET_ONLINE_STATUS: 'SET_ONLINE_STATUS',
  SET_ORDER_HISTORY: 'SET_ORDER_HISTORY',
  ADD_ORDER: 'ADD_ORDER',
  UPDATE_ORDER_STATUS: 'UPDATE_ORDER_STATUS',
  SET_RESERVATION_HISTORY: 'SET_RESERVATION_HISTORY',
  ADD_RESERVATION: 'ADD_RESERVATION',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
};

// Initial app state
const initialAppState = {
  loading: {
    global: false,
    menu: false,
    orders: false,
    reservations: false,
  },
  error: null,
  // Initial state intentionally empty — gets populated from /api/restaurant/info
  // on app load. UI components show '' or skeletons until then.
  restaurantInfo: {
    name: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    coordinates: null,
  },
  operatingHours: {
    monday: { open: '07:00', close: '23:00', isOpen: true },
    tuesday: { open: '07:00', close: '23:00', isOpen: true },
    wednesday: { open: '07:00', close: '23:00', isOpen: true },
    thursday: { open: '07:00', close: '23:00', isOpen: true },
    friday: { open: '07:00', close: '23:00', isOpen: true },
    saturday: { open: '07:00', close: '23:00', isOpen: true },
    sunday: { open: '07:00', close: '23:00', isOpen: true },
  },
  publicSettings: {
    // Populated from /api/settings/public on app init. Shape:
    //   { general: {...}, operations: {...}, payment: {...}, menu: {...} }
    general: {},
    operations: {},
    payment: {
      cash_enabled: true,
      card_enabled: true,
      digital_wallets_enabled: true,
      jazzcash_enabled: true,
      service_charge_percent: 10,
      tax_percent: 10,
    },
    menu: {},
  },
  menuCategories: [
    { key: 'starters', name: 'Starters', count: 0 },
    { key: 'main-course', name: 'Main Course', count: 0 },
    { key: 'desserts', name: 'Desserts', count: 0 },
    { key: 'beverages', name: 'Beverages', count: 0 },
    { key: 'salads', name: 'Salads', count: 0 },
  ],
  specialOffers: [],
  isOnline: navigator.onLine,
  isRestaurantOpen: true,
  orderHistory: [],
  currentOrder: null,
  reservationHistory: [],
  currentReservation: null,
  notification: null,
  settings: {
    currency: 'PKR',
    locale: 'en-US',
    theme: 'light',
    enableNotifications: true,
    enableLocationServices: false,
  },
};

// App reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case APP_ACTIONS.SET_LOADING: {
      const { loadingType, isLoading } = action.payload;
      return {
        ...state,
        loading: {
          ...state.loading,
          [loadingType]: isLoading,
        },
      };
    }
    case APP_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload.error,
      };
    case APP_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    case APP_ACTIONS.SET_RESTAURANT_INFO:
      return {
        ...state,
        restaurantInfo: {
          ...state.restaurantInfo,
          ...action.payload.restaurantInfo,
        },
      };
    case APP_ACTIONS.SET_OPERATING_HOURS:
      return {
        ...state,
        operatingHours: action.payload.operatingHours,
        isRestaurantOpen: action.payload.isOpen,
      };

    case APP_ACTIONS.SET_PUBLIC_SETTINGS:
      return {
        ...state,
        publicSettings: {
          ...state.publicSettings,
          ...(action.payload.publicSettings || {}),
        },
      };
    case APP_ACTIONS.SET_MENU_CATEGORIES:
      return {
        ...state,
        menuCategories: action.payload.categories,
      };
    case APP_ACTIONS.SET_SPECIAL_OFFERS:
      return {
        ...state,
        specialOffers: action.payload.offers,
      };
    case APP_ACTIONS.SET_ONLINE_STATUS:
      return {
        ...state,
        isOnline: action.payload.isOnline,
      };
    case APP_ACTIONS.SET_ORDER_HISTORY:
      return {
        ...state,
        orderHistory: action.payload.orders,
      };
    case APP_ACTIONS.ADD_ORDER:
      return {
        ...state,
        orderHistory: [action.payload.order, ...state.orderHistory],
        currentOrder: action.payload.order,
      };
    case APP_ACTIONS.UPDATE_ORDER_STATUS: {
      const { orderId, status } = action.payload;
      return {
        ...state,
        orderHistory: state.orderHistory.map((order) =>
          order.id === orderId ? { ...order, status } : order
        ),
        currentOrder:
          state.currentOrder?.id === orderId
            ? { ...state.currentOrder, status }
            : state.currentOrder,
      };
    }
    case APP_ACTIONS.SET_RESERVATION_HISTORY:
      return {
        ...state,
        reservationHistory: action.payload.reservations,
      };
    case APP_ACTIONS.ADD_RESERVATION:
      return {
        ...state,
        reservationHistory: [action.payload.reservation, ...state.reservationHistory],
        currentReservation: action.payload.reservation,
      };
    case APP_ACTIONS.SET_NOTIFICATION:
      return {
        ...state,
        notification: action.payload.notification,
      };
    case APP_ACTIONS.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: null,
      };
    default:
      return state;
  }
};

// Create contexts
const AppContext = createContext();
const AuthContext = createContext();
const CartContext = createContext();

// =============================================
// AUTH CONTEXT
// =============================================

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


// AUDIT FIX (H-11 + cross-origin follow-up):
// AuthContext uses raw fetch() rather than the ApiService interceptor.
// To make CSRF + credentials work consistently, wrap every fetch through
// this helper. It:
//   * sets credentials: 'include' so the scr_csrf cookie travels both ways
//   * primes the CSRF token if it's not in the in-memory cache yet
//   * attaches X-CSRF-Token on state-changing requests
//   * retries once on 403 after re-priming the token
function _readCsrfCookie() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)scr_csrf=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}
let _ctxCsrfCache = null;
let _ctxCsrfPrime = null;
async function _ctxPrimeCsrf() {
  if (_ctxCsrfCache) return _ctxCsrfCache;
  if (_ctxCsrfPrime) return _ctxCsrfPrime;
  _ctxCsrfPrime = fetch(`${API_URL}/csrf`, { credentials: 'include' })
    .then(r => r.ok ? r.json() : null)
    .then(j => { _ctxCsrfCache = (j && j.csrfToken) || _readCsrfCookie() || null; return _ctxCsrfCache; })
    .catch(() => { _ctxCsrfCache = _readCsrfCookie() || null; return _ctxCsrfCache; })
    .finally(() => { _ctxCsrfPrime = null; });
  return _ctxCsrfPrime;
}
async function authFetch(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const isWrite = !['GET', 'HEAD', 'OPTIONS'].includes(method);
  if (isWrite) await _ctxPrimeCsrf();
  const token = _ctxCsrfCache || _readCsrfCookie();
  const finalOpts = {
    ...options,
    credentials: 'include',
    headers: {
      ...(options.headers || {}),
      ...(token && isWrite ? { 'X-CSRF-Token': token } : {}),
    },
  };
  let res = await fetch(url, finalOpts);
  if (res.status === 403 && isWrite) {
    _ctxCsrfCache = null;
    await _ctxPrimeCsrf();
    const tok2 = _ctxCsrfCache || _readCsrfCookie();
    if (tok2) {
      const retryOpts = { ...finalOpts, headers: { ...finalOpts.headers, 'X-CSRF-Token': tok2 } };
      res = await fetch(url, retryOpts);
    }
  }
  return res;
}


// Custom error class for auth-related errors
class AuthError extends Error {
  constructor(message, code, details = {}) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.details = details;
  }
}

// =============================================
// CART CONTEXT
// =============================================

// =============================================
// APP PROVIDER
// =============================================

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialAppState);

  // Initialize app data on mount
  useEffect(() => {
    initializeApp();
    setupOnlineStatusListener();
  }, []);

  // Initialize app with data from APIs
  const initializeApp = async () => {
    setLoading('global', true);
    try {
      await Promise.all([
        loadRestaurantInfo(),
        loadOperatingHours(),
        loadPublicSettings(),
        loadSpecialOffers(),
      ]);
    } catch (error) {
      console.error('Error initializing app:', error);
      setError(error.message);
    } finally {
      setLoading('global', false);
    }
  };

  // Set up online/offline status listener
  const setupOnlineStatusListener = () => {
    const handleOnline = () => setOnlineStatus(true);
    const handleOffline = () => setOnlineStatus(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  };

  // Action creators
  const setLoading = (loadingType, isLoading) => {
    dispatch({
      type: APP_ACTIONS.SET_LOADING,
      payload: { loadingType, isLoading },
    });
  };

  const setError = (error) => {
    dispatch({
      type: APP_ACTIONS.SET_ERROR,
      payload: { error },
    });
  };

  const clearError = () => {
    dispatch({ type: APP_ACTIONS.CLEAR_ERROR });
  };

  const setOnlineStatus = (isOnline) => {
    dispatch({
      type: APP_ACTIONS.SET_ONLINE_STATUS,
      payload: { isOnline },
    });
  };

  const showNotification = (message, type = 'info', duration = 5000) => {
    const notification = {
      id: Date.now(),
      message,
      type,
      duration,
    };
    dispatch({
      type: APP_ACTIONS.SET_NOTIFICATION,
      payload: { notification },
    });
    if (duration > 0) {
      setTimeout(() => {
        dispatch({ type: APP_ACTIONS.CLEAR_NOTIFICATION });
      }, duration);
    }
  };

  const clearNotification = () => {
    dispatch({ type: APP_ACTIONS.CLEAR_NOTIFICATION });
  };

  // API loading functions
  const loadRestaurantInfo = async () => {
    try {
      const restaurantInfo = await ApiService.getRestaurantInfo();
      if (restaurantInfo) {
        dispatch({
          type: APP_ACTIONS.SET_RESTAURANT_INFO,
          payload: { restaurantInfo },
        });
      }
    } catch (error) {
      console.error('Error loading restaurant info:', error);
      showNotification('Failed to load restaurant info', 'error');
    }
  };

  const loadOperatingHours = async () => {
    try {
      const hours = await ApiService.getOperatingHours();
      if (hours) {
        const isOpen = isRestaurantCurrentlyOpen();
        dispatch({
          type: APP_ACTIONS.SET_OPERATING_HOURS,
          payload: { operatingHours: hours, isOpen },
        });
      }
    } catch (error) {
      console.error('Error loading operating hours:', error);
      showNotification('Failed to load operating hours', 'error');
    }
  };

  const loadPublicSettings = async () => {
    try {
      const settings = await ApiService.getPublicSettings();
      if (settings) {
        dispatch({
          type: APP_ACTIONS.SET_PUBLIC_SETTINGS,
          payload: { publicSettings: settings },
        });
      }
    } catch (error) {
      // Non-fatal — frontend has hardcoded defaults in initialAppState.
      console.error('Error loading public settings:', error);
    }
  };

  // Helper for components: getSetting('payment', 'service_charge_percent', 10)
  const getSetting = (category, key, defaultValue = null) => {
    const cat = state.publicSettings?.[category];
    if (!cat) return defaultValue;
    return cat[key] !== undefined ? cat[key] : defaultValue;
  };

  const loadSpecialOffers = async () => {
    try {
      const offers = await ApiService.getSpecialOffers();
      dispatch({
        type: APP_ACTIONS.SET_SPECIAL_OFFERS,
        payload: { offers },
      });
    } catch (error) {
      console.error('Error loading special offers:', error);
      showNotification('Failed to load special offers', 'error');
    }
  };

  // Order management
  const addOrder = (order) => {
    dispatch({
      type: APP_ACTIONS.ADD_ORDER,
      payload: { order },
    });
    showNotification('Order placed successfully!', 'success');
  };

  const updateOrderStatus = (orderId, status) => {
    dispatch({
      type: APP_ACTIONS.UPDATE_ORDER_STATUS,
      payload: { orderId, status },
    });
    const statusMessages = {
      confirmed: 'Order confirmed!',
      preparing: 'Your order is being prepared',
      ready: 'Order is ready for pickup',
      delivered: 'Order delivered successfully',
      cancelled: 'Order has been cancelled',
    };
    const message = statusMessages[status] || `Order status updated to ${status}`;
    showNotification(message, 'info');
  };

  const loadOrderHistory = async (customerInfo) => {
    setLoading('orders', true);
    try {
      const orders = await ApiService.getOrderHistory(customerInfo);
      dispatch({
        type: APP_ACTIONS.SET_ORDER_HISTORY,
        payload: { orders },
      });
    } catch (error) {
      console.error('Error loading order history:', error);
      setError('Failed to load order history');
      showNotification('Failed to load order history', 'error');
    } finally {
      setLoading('orders', false);
    }
  };

  // Reservation management
  const addReservation = (reservation) => {
    dispatch({
      type: APP_ACTIONS.ADD_RESERVATION,
      payload: { reservation },
    });
    showNotification('Reservation confirmed!', 'success');
  };

  const loadReservationHistory = async (customerInfo) => {
    setLoading('reservations', true);
    try {
      const reservations = await ApiService.getReservationHistory(customerInfo);
      dispatch({
        type: APP_ACTIONS.SET_RESERVATION_HISTORY,
        payload: { reservations },
      });
    } catch (error) {
      console.error('Error loading reservation history:', error);
      setError('Failed to load reservation history');
      showNotification('Failed to load reservation history', 'error');
    } finally {
      setLoading('reservations', false);
    }
  };

  // Utility functions
  const isRestaurantCurrentlyOpen = () => {
    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const currentTime = now.getHours() * 100 + now.getMinutes();

    const todayHours = state.operatingHours[currentDay];
    if (!todayHours || !todayHours.isOpen) {
      return false;
    }

    const openTime = parseInt(todayHours.open.replace(':', ''));
    const closeTime = parseInt(todayHours.close.replace(':', ''));

    return currentTime >= openTime && currentTime <= closeTime;
  };

  // Format `amount` as money using the currency configured in the admin
  // Settings panel. The currency is read from publicSettings.payment.currency
  // (with menu.currency_symbol as a legacy fallback) so a Settings change is
  // reflected everywhere on next reload. Output format is ISO + 2 decimals
  // (e.g. "PKR 3,450.00") via the shared helper in utils.
  const formatCurrency = (amount) => {
    const code = getCurrencyCode(state.publicSettings) || state.settings.currency;
    return sharedFormatCurrency(amount, code, state.settings.locale);
  };

  // The active ISO currency code, exposed so components can render labels
  // (e.g. "Price (PKR)") without re-deriving it themselves.
  const currencyCode = getCurrencyCode(state.publicSettings) || state.settings.currency;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(state.settings.locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString(state.settings.locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Context value
  const contextValue = {
    ...state,
    setLoading,
    setError,
    clearError,
    showNotification,
    clearNotification,
    loadRestaurantInfo,
    loadOperatingHours,
    loadPublicSettings,
    getSetting,
    loadSpecialOffers,
    addOrder,
    updateOrderStatus,
    loadOrderHistory,
    addReservation,
    loadReservationHistory,
    isRestaurantCurrentlyOpen,
    formatCurrency,
    currencyCode,
    formatDate,
    formatTime,
  };

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

// =============================================
// AUTH PROVIDER - FIXED VERSION
// =============================================

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [mfaMethod, setMfaMethod] = useState(null);
  const [mfaUserId, setMfaUserId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const navigate = useNavigate();

  // Generate a persistent device ID
  const getDeviceId = useCallback(() => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }, []);

  // Enhanced token verification with refresh fallback
  const verifyToken = useCallback(async (token) => {
    try {
      const response = await authFetch(`${API_URL}/auth/check-verification`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'X-Device-Id': getDeviceId()
        }
      });

      if (response.ok) {
        const data = await response.json();
        return { valid: true, user: data.user };
      }
      
      if (response.status === 401) {
        return { valid: false, shouldRefresh: true };
      }
      
      return { valid: false };
    } catch (error) {
      console.error('Token verification failed:', error);
      return { valid: false };
    }
  }, [getDeviceId]);

  // Enhanced refresh token logic with device tracking
  const refreshAuthToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return false;

      const response = await authFetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Device-Id': getDeviceId()
        },
        body: JSON.stringify({ 
          refreshToken,
          deviceFingerprint: navigator.userAgent
        })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('authToken', data.accessToken);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        setUser(data.user);
        return true;
      }
      
      // If refresh fails, clear tokens
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [getDeviceId]);

  // Fetch active sessions
  const fetchSessions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await authFetch(`${API_URL}/auth/sessions`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Device-Id': getDeviceId()
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, [getDeviceId]);

  // Single comprehensive checkAuth function
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (token) {
        const { valid, user: verifiedUser, shouldRefresh } = await verifyToken(token);
        
        if (valid) {
          // Store user in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(verifiedUser));
          setUser(verifiedUser);
          await fetchSessions();
          return;
        }

        if (shouldRefresh) {
          const refreshed = await refreshAuthToken();
          if (refreshed) {
            // After refresh, get the user from localStorage
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              setUser(JSON.parse(storedUser));
            }
            await fetchSessions();
            return;
          }
        }

        // Clear everything if token is invalid
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        setUser(null);
        setSessions([]);
      } else {
        // Check if user exists in localStorage but tokens are missing
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          localStorage.removeItem('user'); // Clear stale user data
        }
        setUser(null);
        setSessions([]);
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setError(err.message);
      // Clear on error
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [verifyToken, refreshAuthToken, fetchSessions]);

  // Initialize auth on mount
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      
      // First, check localStorage for existing user
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('authToken');
      
      if (storedUser && token) {
        try {
          const parsedUser = JSON.parse(storedUser);
          
          // Verify the token is still valid
          const { valid } = await verifyToken(token);
          
          if (valid) {
            // Token is valid, set the user
            setUser(parsedUser);
            await fetchSessions();
          } else {
            // Token invalid, try to refresh
            const refreshed = await refreshAuthToken();
            if (refreshed) {
              // After refresh, get the updated user from localStorage
              const updatedUser = localStorage.getItem('user');
              if (updatedUser) {
                setUser(JSON.parse(updatedUser));
              }
              await fetchSessions();
            } else {
              // Refresh failed, clear everything
              localStorage.removeItem('user');
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
              setUser(null);
            }
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          setUser(null);
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
    
    // Set up periodic auth check (every 5 minutes)
    const interval = setInterval(() => {
      const token = localStorage.getItem('authToken');
      if (token) {
        checkAuth();
      }
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [checkAuth, fetchSessions, refreshAuthToken, verifyToken]);

  // Helper function for OAuth login
  const loginWithOAuth = useCallback((userData, tokens) => {
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('authToken', tokens.accessToken);
    if (tokens.refreshToken) {
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    setUser(userData);
    
    // Fetch sessions after OAuth login
    fetchSessions();
  }, [fetchSessions]);

  // Enhanced login with MFA support and device tracking
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      
      const deviceId = getDeviceId();
      const deviceInfo = {
        os: navigator.platform,
        browser: navigator.userAgent,
        ip: '' // Will be set by backend
      };

      const response = await authFetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Device-Id': deviceId
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
          deviceId,
          deviceInfo,
          deviceFingerprint: navigator.userAgent,
          rememberMe: credentials.rememberMe || false
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new AuthError(
          data.message || 'Login failed',
          data.code || 'LOGIN_FAILED',
          { status: response.status }
        );
      }

      // Handle MFA requirement
      if (data.mfaRequired) {
        setRequiresMFA(true);
        setMfaMethod(data.mfaMethod);
        setMfaUserId(data.userId);
        return { 
          success: true, 
          requiresMFA: true,
          mfaMethod: data.mfaMethod 
        };
      }

      localStorage.setItem('authToken', data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      // Store user in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setUser(data.user);
      setRequiresMFA(false);
      await fetchSessions();
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { 
        success: false, 
        error: err.message,
        code: err.code,
        details: err.details
      };
    } finally {
      setLoading(false);
    }
  };

  // MFA verification with device trust option
  const verifyMFA = async (code, trustDevice = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const deviceId = getDeviceId();
      const response = await authFetch(`${API_URL}/auth/verify-mfa`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Device-Id': deviceId
        },
        body: JSON.stringify({
          userId: mfaUserId,
          code,
          deviceId,
          deviceFingerprint: navigator.userAgent,
          trustDevice
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new AuthError(
          data.message || 'MFA verification failed',
          data.code || 'MFA_FAILED',
          { attemptsRemaining: data.attemptsRemaining }
        );
      }

      localStorage.setItem('authToken', data.accessToken);
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      // Store user in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setUser(data.user);
      setRequiresMFA(false);
      await fetchSessions();
      return { 
        success: true,
        backupCodeUsed: data.backupCodeUsed,
        remainingBackupCodes: data.remainingBackupCodes
      };
    } catch (err) {
      setError(err.message);
      return { 
        success: false, 
        error: err.message,
        code: err.code,
        details: err.details
      };
    } finally {
      setLoading(false);
    }
  };

  // Enhanced logout with session management
  const logout = async (logoutAll = false, redirectPath = '/login') => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        await authFetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Device-Id': getDeviceId()
          },
          body: JSON.stringify({ 
            logoutAll,
            deviceId: getDeviceId()
          })
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      setError(null);
      setRequiresMFA(false);
      setSessions([]);
      navigate(redirectPath);
    }
  };

  // Revoke specific session
  const revokeSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return false;

      const response = await authFetch(`${API_URL}/auth/revoke-session`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Device-Id': getDeviceId()
        },
        body: JSON.stringify({ sessionId })
      });

      if (response.ok) {
        await fetchSessions();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to revoke session:', error);
      return false;
    }
  };

  // Email verification check
  const checkEmailVerification = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return false;
      
      const response = await authFetch(`${API_URL}/auth/check-verification`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Device-Id': getDeviceId()
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.emailVerified;
      }
      return false;
    } catch (error) {
      console.error('Verification check failed:', error);
      return false;
    }
  };

  // Resend verification email
  const resendVerification = async (email) => {
    try {
      setLoading(true);
      const response = await authFetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Device-Id': getDeviceId()
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new AuthError(
          data.message || 'Failed to resend verification',
          data.code || 'VERIFICATION_FAILED'
        );
      }

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { 
        success: false, 
        error: err.message,
        code: err.code
      };
    } finally {
      setLoading(false);
    }
  };

  // Password reset request
  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      const response = await authFetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Device-Id': getDeviceId()
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new AuthError(
          data.message || 'Password reset failed',
          data.code || 'PASSWORD_RESET_FAILED'
        );
      }

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { 
        success: false, 
        error: err.message,
        code: err.code
      };
    } finally {
      setLoading(false);
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) throw new AuthError('Not authenticated', 'UNAUTHENTICATED');

      const response = await authFetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Device-Id': getDeviceId()
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new AuthError(
          data.message || 'Password change failed',
          data.code || 'PASSWORD_CHANGE_FAILED',
          { requirements: data.requirements }
        );
      }

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { 
        success: false, 
        error: err.message,
        code: err.code,
        details: err.details
      };
    } finally {
      setLoading(false);
    }
  };

  // Update user data (for OAuth callback)
  const updateUser = useCallback((userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const value = {
    user,
    setUser,
    updateUser, // Add this for OAuth callback
    loginWithOAuth, // Add this for OAuth callback
    loading,
    error,
    requiresMFA,
    mfaMethod,
    sessions,
    login,
    verifyMFA,
    logout,
    revokeSession,
    checkAuth,
    checkEmailVerification,
    resendVerification,
    forgotPassword,
    changePassword,
    isAuthenticated: !!user,
    getDeviceId
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// =============================================
// CART PROVIDER
// =============================================

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('restaurant_cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(parsedCart);
      }
    } catch (error) {
      console.error('Failed to load cart from localStorage:', error);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('restaurant_cart', JSON.stringify(cartItems));
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
    }
  }, [cartItems]);

  // Generate unique cart ID for each item
  const generateCartId = () => {
    return `cart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Add item to cart
  const addToCart = useCallback((item, tableNumber = null) => {
    setCartItems(prevItems => {
      const itemId = item._id || item.id || item.uniqueId;
      
      // Check if item already exists in cart
      const existingItemIndex = prevItems.findIndex(cartItem => 
        (cartItem._id || cartItem.id) === itemId
      );

      if (existingItemIndex !== -1) {
        // Item exists, increment quantity
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + 1
        };
        return updatedItems;
      } else {
        // Item doesn't exist, add new item
        const newCartItem = {
          ...item,
          cartId: generateCartId(),
          quantity: 1,
          tableNumber: tableNumber,
          addedAt: Date.now()
        };
        return [...prevItems, newCartItem];
      }
    });
  }, []);

  // Remove item from cart (removes one quantity or entire item if quantity is 1)
  const removeFromCart = useCallback((cartId) => {
    setCartItems(prevItems => {
      const itemIndex = prevItems.findIndex(item => item.cartId === cartId);
      
      if (itemIndex === -1) return prevItems;

      const updatedItems = [...prevItems];
      const item = updatedItems[itemIndex];

      if (item.quantity > 1) {
        // Decrease quantity
        updatedItems[itemIndex] = {
          ...item,
          quantity: item.quantity - 1
        };
        return updatedItems;
      } else {
        // Remove item completely
        return updatedItems.filter(item => item.cartId !== cartId);
      }
    });
  }, []);

  // Update item quantity directly
  const updateItemQuantity = useCallback((cartId, newQuantity) => {
    if (newQuantity <= 0) {
      // Remove item if quantity is 0 or less
      setCartItems(prevItems => prevItems.filter(item => item.cartId !== cartId));
    } else {
      setCartItems(prevItems => 
        prevItems.map(item => 
          item.cartId === cartId 
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    }
  }, []);

  // Persist a per-item note (e.g. "no onions"). Stored on the cart item so
  // it flows through to the order payload and onto the kitchen ticket.
  const updateItemSpecialInstructions = useCallback((cartId, instructions) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.cartId === cartId
          ? { ...item, specialInstructions: instructions }
          : item
      )
    );
  }, []);

  // Remove item completely from cart
  const removeItemCompletely = useCallback((cartId) => {
    setCartItems(prevItems => prevItems.filter(item => item.cartId !== cartId));
  }, []);

  // Clear entire cart
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // Get item from cart by menu item ID
  const getItemInCart = useCallback((menuItemId) => {
    return cartItems.find(cartItem => 
      (cartItem._id || cartItem.id) === menuItemId
    );
  }, [cartItems]);

  // Get total number of items in cart
  const getCartItemCount = useCallback(() => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  }, [cartItems]);

  // Get total price of all items in cart
  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  }, [cartItems]);

  // Get unique item count (number of different items)
  const getUniqueItemCount = useCallback(() => {
    return cartItems.length;
  }, [cartItems]);

  // Validate cart before checkout
  const validateCart = useCallback((customerInfo = {}) => {
    const errors = [];

    if (cartItems.length === 0) {
      errors.push('Your cart is empty');
      return { isValid: false, errors };
    }

    // Validate customer info if provided
    if (customerInfo.customerName && !customerInfo.customerName.trim()) {
      errors.push('Customer name is required');
    }

    if (customerInfo.phoneNumber && !customerInfo.phoneNumber.trim()) {
      errors.push('Phone number is required');
    }

    // Check if all items are still available (this would need API call in real scenario)
    const unavailableItems = cartItems.filter(item => 
      item.available === false || item.isAvailable === false
    );

    if (unavailableItems.length > 0) {
      unavailableItems.forEach(item => {
        errors.push(`${item.name} is no longer available`);
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [cartItems]);

  // Get order data formatted for API
  const getOrderData = useCallback((customerInfo = {}) => {
    return {
      items: cartItems.map(item => ({
        menuItem: item._id || item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        specialInstructions: item.specialInstructions || ''
      })),
      customerInfo,
      tableNumber: cartItems[0]?.tableNumber || null,
      subtotal: getCartTotal(),
      tax: getCartTotal() * 0.1, // 10% tax
      total: getCartTotal() * 1.1,
      orderType: 'dine-in', // This could be dynamic
      source: 'web'
    };
  }, [cartItems, getCartTotal]);

  // Check if cart has items
  const hasItems = cartItems.length > 0;

  // Get cart summary
  const getCartSummary = useCallback(() => {
    return {
      itemCount: getCartItemCount(),
      uniqueItemCount: getUniqueItemCount(),
      total: getCartTotal(),
      items: cartItems
    };
  }, [cartItems, getCartItemCount, getUniqueItemCount, getCartTotal]);

  const cart = {
    items: cartItems,
    showCart: false, // This might be managed by the component using the cart
    hasItems,
    getOrderData,
    clearCart
  };

  const value = {
    cart,
    cartItems,
    addToCart,
    removeFromCart,
    updateItemQuantity,
    updateItemSpecialInstructions,
    removeItemCompletely,
    clearCart,
    getItemInCart,
    getCartItemCount,
    getCartTotal,
    getUniqueItemCount,
    validateCart,
    getOrderData,
    getCartSummary,
    hasItems
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// =============================================
// PROTECTED ROUTE COMPONENT - UPDATED
// =============================================

export const ProtectedRoute = ({ 
  children, 
  roles = [], 
  requireVerifiedEmail = false,
  redirectTo = '/login',
  fallback = null
}) => {
  const { 
    user, 
    isAuthenticated, 
    loading,
    checkEmailVerification
  } = useAuth();
  const navigate = useNavigate();
  const [emailVerified, setEmailVerified] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const verifyAuth = async () => {
      if (requireVerifiedEmail && isAuthenticated) {
        const verified = await checkEmailVerification();
        setEmailVerified(verified);
        if (!verified) {
          navigate('/verify-email');
          return;
        }
      }
      setAuthChecked(true);
    };

    if (!loading) {
      verifyAuth();
    }
  }, [isAuthenticated, requireVerifiedEmail, checkEmailVerification, navigate, loading]);

  useEffect(() => {
    if (!loading && !isAuthenticated && authChecked) {
      navigate(redirectTo, { replace: true });
    } else if (!loading && isAuthenticated && roles.length > 0 && !roles.includes(user?.role)) {
      navigate('/unauthorized', { replace: true });
    }
  }, [isAuthenticated, loading, navigate, redirectTo, roles, user, authChecked]);

  if (loading) return fallback || <div>Loading...</div>;
  if (!isAuthenticated) return null;
  if (roles.length > 0 && !roles.includes(user.role)) return null;
  if (requireVerifiedEmail && !emailVerified) return null;

  return children;
};

// =============================================
// CUSTOM HOOKS
// =============================================

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default {
  AppProvider,     // Already exported at line 13
  AuthProvider,    // Already exported at line 488  
  CartProvider,    // Already exported at line 924
  ProtectedRoute,  // Already exported at line 1117
  useApp,          // Already exported at line 1143
  useAuth,         // Already exported at line 1150
  useCart,         // Already exported at line 1157
  AppContext,      // Already exported at line 58
  AuthContext,     // Already exported at line 59
  CartContext,     // Already exported at line 60
  APP_ACTIONS      // Already exported at line 5
};